import {useAtom, useAtomValue} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Button, IconButton} from '@/components/Button';
import {InfoIconComponent, MapFillIconComponent} from '@/components/Icons';
import {LoadingBar} from '@/components/LoadingBar';
import {SelectList} from '@/components/SelectList';
import {Typography, typographyStyle} from '@/components/Typography';
import {MEDALS} from '@/constants';
import {useGA4WithParams} from '@/hooks/tracking';
import {KodataCategoryGroup} from '@/map/@types';
import {useQueryRegionByPoint, useTopSalesCategory} from '@/map/api';
import {mapCenterAtom, mapInstanceAtom, rankingBoardCategoryAtom} from '@/map/atom';
import {MAP_QUERY_DEBOUNCE_MS} from '@/map/constants';
import {GeoDataType} from '@/map/enums';
import {
  flattenPolygonToNaverCoords, getGeometryCenterPoint, getSidoShortName, getZoomLevel
} from '@/map/utils';
import {Flexbox, FlexCenter, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber} from '@/utils';
import {CategorySelectWindow} from '../CategorySelectWindow';
import IconRanking from './icon-ranking.svg';
import {InfoModal} from './InfoModal';
import {RankingBlockLayer} from './MapLayer';
import IconRotateRight from './rotate-right.svg';

interface IProps {
  onClose(): void;
}

export const RankingBoard = ({onClose}: IProps) => {
  const mapCenter = useAtomValue(mapCenterAtom);
  const {data: point} = useQueryRegionByPoint(
    !!mapCenter && {lng: mapCenter.x, lat: mapCenter.y, type: GeoDataType.Emd},
    MAP_QUERY_DEBOUNCE_MS
  );

  const map = useAtomValue(mapInstanceAtom);

  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [openCategorySelectModal, setOpenCategoryModal] =
    useState<boolean>(false);

  const [geodataVar, setGeodataVar] = useState<{
    name: string;
    code: string;
  }>();

  const [selectedBlock, setSelectedBlock] = useState<number>(-1);
  const [hoveredBlock, setHoveredBlock] = useState<number>(-1);

  const [selectedCategory, setSelectedCategory] = useAtom(
    rankingBoardCategoryAtom
  );

  const pointProperties = point;

  const idleEvent = useRef<naver.maps.MapEventListener>();

  const hasCategoryDetail = !!selectedCategory?.categoryDetail;

  const enabled = !!(hasCategoryDetail && geodataVar?.code);

  const tracking = useGA4WithParams();

  const {data, isFetching} = useTopSalesCategory(
    enabled
      ? {
          categories: [selectedCategory],
          sggCodes: [geodataVar?.code],
        }
      : undefined
  );

  const onSelectItem = useCallback((id: number) => {
    setSelectedBlock(id);
  }, []);

  const onHoverItem = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rank = e.currentTarget.dataset.rank;
    const id = Number(rank);

    if (e.type === 'mouseenter') {
      setHoveredBlock(id);
    } else {
      setHoveredBlock((prev) => (prev === id ? -1 : prev));
    }
  }, []);

  const onClickTopItem = (e: React.MouseEvent<HTMLDivElement>) => {
    const rank = e.currentTarget.dataset.rank;
    const id = Number(rank);
    const item = data?.[id];

    if (item) {
      const polygon = item.geometry?.coordinates?.[0];

      if (polygon && map) {
        const center = getGeometryCenterPoint(item.geometry.coordinates);

        if (center) {
          map.setCenter(center);
          map.setZoom(getZoomLevel(100));
        }
      }

      setSelectedBlock(id);
      if (geodataVar?.name) {
        tracking('rankingboard_shortcut', {address: geodataVar.name});
      }
    }
  };

  const onSelectedCategory = (selected: KodataCategoryGroup[]) => {
    if (selected?.length) {
      const [category] = selected;

      setSelectedCategory(category);
      tracking('rankingboard_use', {
        category_2depth_array: category.categoryDetail,
      });
    }
    setOpenCategoryModal(false);
  };

  const onIdle = useCallback(() => {
    if (selectedBlock && data && map) {
      const item = data[selectedBlock];

      if (item?.geometry) {
        const polygon = item.geometry?.coordinates?.[0];
        const hasBounds = map
          .getBounds()
          .intersects(flattenPolygonToNaverCoords(polygon) as any);

        if (!hasBounds) {
          setSelectedBlock(-1);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock]);

  const refetchWithCurrentPoint = () => {
    if (point) {
      const {sggCode, sgg, sido} = point;

      if (sggCode && sgg && sido) {
        setGeodataVar((prev) =>
          prev?.code === sggCode
            ? prev
            : {
                name: `${getSidoShortName(sido)} ${sgg}`,
                code: sggCode,
              }
        );
      }
    }
  };

  useEffect(() => {
    if (!geodataVar?.code) {
      refetchWithCurrentPoint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point]);

  useEffect(() => {
    setSelectedBlock(-1);
  }, [data]);

  useEffect(() => {
    if (map && selectedBlock) {
      idleEvent.current = naver.maps.Event.addListener(map, 'idle', onIdle);
    }

    return () => {
      if (idleEvent.current) {
        naver.maps.Event.removeListener(idleEvent.current);
        idleEvent.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock]);

  const categoryOption = hasCategoryDetail
    ? [
        {
          value: selectedCategory.categoryDetail,
          label: selectedCategory.categoryDetail,
        },
      ]
    : undefined;

  const hasData = !!data?.length;

  return (
    <Root>
      <Head>
        <img src={IconRanking} alt="랭킹" />
        <Typography
          variant="medium14"
          color={palette.textPrimary}
          fontWeight="700"
        >
          매출이 높은 상권
        </Typography>
        <Typography variant="medium12" color="#FF7152">
          Beta
        </Typography>
        <InfoIconComponent
          style={{cursor: 'pointer'}}
          onClick={() => setOpenInfo(true)}
        />
        <FlexFullGrow />
        <Button variant="outline" size="small" onClick={onClose}>
          접기
        </Button>
      </Head>
      <Body>
        <SelectSection>
          <Address>
            <MapFillIconComponent color={palette.textPrimary} />
            <Typography variant="medium14" color={palette.textPrimary}>
              {geodataVar?.name}
            </Typography>
          </Address>
          <SelectList
            label="업종"
            placeholder="업종을 선택해주세요"
            list={categoryOption}
            selected={categoryOption}
            readOnly
            onClick={() => setOpenCategoryModal(true)}
          />
        </SelectSection>
        <PaddingBox spacing={8}>
          {isFetching ? (
            <LoadingBar size={40} color={palette.gray800} />
          ) : hasData ? (
            data.slice(0, 3).map((item, rank) => (
              <Item
                key={rank}
                data-rank={rank}
                onClick={onClickTopItem}
                onMouseEnter={onHoverItem}
                onMouseLeave={onHoverItem}
              >
                <Rank>{MEDALS[rank]}</Rank>
                <Sales>{humanizeNumber(item.averageEstSale, true)}</Sales>
                <FlexFullGrow />
                <Button
                  variant="ghost"
                  color="linkPrimary"
                  size="small"
                  noPadding
                >
                  바로가기
                </Button>
              </Item>
            ))
          ) : (
            enabled && (
              <PaddingBox spacing={12}>
                <FlexCenter>
                  <Typography variant="medium14">
                    선택 업종에 대한 결과가 없습니다.
                  </Typography>
                </FlexCenter>
              </PaddingBox>
            )
          )}
          <PaddingBox top={8}>
            {!!pointProperties &&
              !!geodataVar &&
              pointProperties.sggCode !== geodataVar.code && (
                <IconButton
                  icon={IconRotateRight}
                  size="small"
                  variant="fill"
                  color="secondary"
                  fullWidth
                  onClick={refetchWithCurrentPoint}
                >
                  <strong>현재 위치에서 검색</strong>
                </IconButton>
              )}
          </PaddingBox>
        </PaddingBox>
      </Body>
      {openCategorySelectModal && (
        <CategorySelectWindow
          maxCount={1}
          defaultOptions={selectedCategory ? [selectedCategory] : undefined}
          onClose={() => setOpenCategoryModal(false)}
          onComplete={(selected) => onSelectedCategory(selected)}
        />
      )}
      {!!map && hasData && (
        <RankingBlockLayer
          map={map}
          items={data}
          activeId={selectedBlock}
          focusId={hoveredBlock}
          onSelectItem={onSelectItem}
        />
      )}
      {openInfo && <InfoModal onClose={() => setOpenInfo(false)} />}
    </Root>
  );
};

const Root = styled.div`
  width: 240px;
  border-radius: 8px;
  background-color: ${palette.white};
  ${{...typographyStyle.medium14}};
  font-weight: 500;
`;

const Head = styled(Flexbox)`
  padding: 8px 12px 8px 8px;
  gap: 2px;
`;

const Body = styled.div``;

const SelectSection = styled.div`
  padding: 8px 12px;
`;

const Address = styled(Flexbox)`
  color: ${palette.gray800};
  gap: 4px;
  margin-bottom: 12px;
`;

const Rank = styled(FlexCenter)`
  color: ${palette.textSecondary};
  ${{...typographyStyle.medium20}};
`;

const Item = styled(Flexbox)`
  width: 100%;
  height: 40px;
  cursor: pointer;
  gap: 8px;
  padding: 0 8px;
  ${{...typographyStyle.medium14}};
  border-radius: 5px;
  ${Button} {
    opacity: 0;
  }
  &:hover {
    background-color: ${palette.gray100};
    ${Button} {
      opacity: 1;
    }
  }
`;

const Sales = styled.div`
  color: ${palette.textPrimary};
  font-weight: 700;
`;
